import { useEffect, useState } from "react";
import { api, formatINR, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ShieldCheck, CheckCircle2, XCircle, User, Loader2, ChevronDown, ChevronUp,
} from "lucide-react";

export default function AdminApprovals() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [acting, setActing] = useState(null);

  const fetch = () => {
    setLoading(true);
    api.get("/admin/products/pending")
      .then((r) => setProducts(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetch(); }, []);

  const approve = async (pid) => {
    setActing(pid);
    try {
      await api.put(`/admin/products/${pid}/approve`, { action: "approve" });
      toast.success("Product approved and published!");
      fetch();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setActing(null); }
  };

  const reject = async (pid) => {
    setActing(pid);
    try {
      await api.put(`/admin/products/${pid}/approve`, { action: "reject", reason: rejectReason });
      toast.success("Product rejected");
      setRejectReason("");
      fetch();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setActing(null); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-approvals-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-primary" />
            Approval Queue
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Review and approve vendor-submitted listings</p>
        </div>
        <Badge variant="outline" className="text-sm">{products.length} pending</Badge>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2].map(i => <div key={i} className="h-24 bg-muted animate-pulse rounded-xl" />)}</div>
      ) : products.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground">
          <CheckCircle2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>All caught up! No products pending approval.</p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="approval-list">
          {products.map((p) => {
            const expanded = expandedId === p.id;
            return (
              <Card key={p.id} className="overflow-hidden" data-testid={`approval-${p.id}`}>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-4 cursor-pointer" onClick={() => setExpandedId(expanded ? null : p.id)}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-sm">{p.title}</span>
                        <Badge variant="outline" className="text-[10px] capitalize">{p.category}</Badge>
                        {p.sub_category && <Badge variant="secondary" className="text-[10px]">{p.sub_category}</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatINR(p.adult_price)}/adult · {p.destination || "Hyderabad"}
                        {p.vendor && (
                          <span className="ml-2 inline-flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {p.vendor.name || p.vendor.email}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </div>

                  {expanded && (
                    <div className="mt-4 pt-4 border-t border-border space-y-3">
                      <div className="text-sm text-muted-foreground">{p.short_desc || p.description?.slice(0, 200)}</div>
                      {p.featured_image && <img src={p.featured_image} alt="" className="w-full max-h-48 object-cover rounded-lg" />}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                        <div><strong>Duration:</strong> {p.duration || "—"}</div>
                        <div><strong>Min Pax:</strong> {p.min_pax || 1}</div>
                        <div><strong>Adult:</strong> {formatINR(p.adult_price)}</div>
                        <div><strong>Child:</strong> {formatINR(p.child_price || 0)}</div>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-2 pt-2">
                        <Button
                          onClick={() => approve(p.id)}
                          disabled={acting === p.id}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`approve-btn-${p.id}`}
                        >
                          {acting === p.id ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                          Approve & Publish
                        </Button>
                        <div className="flex gap-2 flex-1">
                          <Input
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="Reason for rejection..."
                            className="text-sm"
                            data-testid={`reject-reason-${p.id}`}
                          />
                          <Button
                            variant="destructive"
                            onClick={() => reject(p.id)}
                            disabled={acting === p.id}
                            data-testid={`reject-btn-${p.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
